import { Button, FAIcon } from '@/components'
import { PERMISSIONS, usePermissions } from '@/utils'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ExportFormat } from '@/modules/exports'
import { ExportFormatPrompt } from '@/modules/exports'
import type { VehicleListExportFilters, VehicleListExportKind } from './types'
import { useVehicleListExport } from './use-vehicle-list-export'

interface Props<TKind extends VehicleListExportKind> {
  kind: TKind
  /** The filters the page is querying with — the export gets the same rows. */
  filters: VehicleListExportFilters[TKind]
}

/**
 * The export action in a vehicle list page's header. Renders nothing for a user
 * without the export permission.
 */
export function VehicleListExportButton<TKind extends VehicleListExportKind>({
  kind,
  filters,
}: Props<TKind>) {
  const translations = useTranslate({ export: 'common.export' })

  const { hasPermissions } = usePermissions()
  const addPrompt = usePrompt()

  const { exportList, loading } = useVehicleListExport(kind)

  async function handleExport() {
    const { data: exportFormat } = await addPrompt<ExportFormat | null>(
      resolve => <ExportFormatPrompt onSubmit={resolve} />
    )
    if (!exportFormat) return

    await exportList(exportFormat, filters)
  }

  if (!hasPermissions(PERMISSIONS.companies.export.exportReports, true)) {
    return null
  }

  return (
    <Button variant="outline" disabled={loading} onClick={handleExport}>
      <FAIcon icon="download" />
      {translations.export}
    </Button>
  )
}
